import React, { useCallback, useMemo, useState } from 'react';
import ContentBox from '@root/components/v2/content/ContentBox';
import Text from '@root/components/v2/ui-elements/Text';
import MuiTable, { DEFAULT_PAGE_SIZE } from '@root/components/table/table';
import HeaderBar from '@root/components/HeaderBarNew';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import { UI_COLORS } from '@root/utilities/pastelColors';
import { formatNumber } from './vulnerabilityScanColumns';
import { VulnerabilityCounts } from '../../vulnerabilities/VulnerabilityCounts';

const KindChip = ({ kind }) => (
  <span
    className="rounded-md px-2 py-0.5 text-[12px] font-semibold"
    style={{ backgroundColor: UI_COLORS.background.secondary, color: UI_COLORS.text.secondary }}>
    {kind}
  </span>
);

const workloadColumns = [
  {
    field: 'name',
    headerName: 'Workload',
    minWidth: 200,
    sortable: false,
    renderCell: params => (
      <div className="flex flex-col">
        <Text className="text-[13px]" font="bold" style={{ color: UI_COLORS.text.primary }}>
          {params.row.name}
        </Text>
        <Text className="text-[12px]" font="medium" style={{ color: UI_COLORS.text.secondary }}>
          {params.row.namespace}
        </Text>
      </div>
    ),
  },
  {
    field: 'kind',
    headerName: 'Kind',
    minWidth: 140,
    sortable: false,
    renderCell: params => <KindChip kind={params.row.kind} />,
  },
  {
    field: 'severity',
    headerName: 'Severity',
    minWidth: 350,
    sortable: false,
    renderCell: params => <VulnerabilityCounts vulnerabilities={params.row.severity} />,
  },
  {
    field: 'findings',
    headerName: 'Findings',
    minWidth: 110,
    sortable: false,
    renderCell: params => (
      <Text className="text-[13px]" font="bold" style={{ color: UI_COLORS.text.primary }}>
        {formatNumber(params.row.findings)}
      </Text>
    ),
  },
];

const WorkloadCoverageCard = ({ workloads = [] }) => {
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedSearch = useDebouncedValue(searchString, 300);

  const rows = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return workloads
      .map((w, idx) => ({ ...w, id: `${w.name}-${idx}` }))
      .filter(
        w =>
          !query ||
          w.name?.toLowerCase().includes(query) ||
          w.namespace?.toLowerCase().includes(query) ||
          w.kind?.toLowerCase().includes(query),
      );
  }, [workloads, debouncedSearch]);

  const handleSearchChange = useCallback(e => {
    setSearchString(e.target.value);
    setCurrentPage(1);
  }, []);

  return (
    <ContentBox
      title="Workload Coverage"
      paddingMode="content"
      style={{ backgroundColor: UI_COLORS.background.primary }}>
      <div className="flex flex-col p-4">
        <HeaderBar
          searchBar
          searchBarValue={searchString}
          setSearchTerm={setSearchString}
          searchBarOnChange={handleSearchChange}
          onSearchBarSubmit={e => e.preventDefault()}
          marginBottom={'15px'}
          exportButton={false}
        />
        <MuiTable
          rows={rows}
          columns={workloadColumns}
          LoaderState={false}
          page={currentPage}
          total_size={rows.length}
          pageSize={pageSize}
          rowHeight={56}
          isDynamicHeight={true}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          disableDynamicColumn={true}
          noDataMessage="No workloads found"
        />
      </div>
    </ContentBox>
  );
};

export default WorkloadCoverageCard;
